import { ControlIcons } from 'owa-control-icons';
import { isFeatureEnabled } from 'owa-feature-flags';
import { FilterableMenuItem, FilterableMenuItemArgs } from 'owa-filterable-menu';
import loc from 'owa-localize';
import { EditButtonLabel } from 'owa-locstrings/lib/strings/editbuttonlabel.locstring.json';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import {
    EditMode,
    getOrCreateSxSStoreData,
    SxSStoreData,
    lazySaveToCloudFromSxS,
} from 'owa-sxs-store';
import { PreviewPaneMode } from 'owa-sxsdata';
import {
    EditAndReplyButtonLabel,
    EditInBrowserButtonLabel,
    EditInDesktopButtonLabel,
    EditInBrowserUploadingText,
    EditInBrowserOpeningText,
    EditInBrowserErrorText,
} from './getEditButtons.locstring.json';
import { isCloudyAndUsingJsApi, editAndReply } from './getEditButtons';
import * as React from 'react';
import { FontIcon, ImageIcon } from '@fluentui/react/lib/Icon';
import { appPowerPoint } from 'owa-locstrings/lib/strings/apppowerpoint.locstring.json';
import { appExcel } from 'owa-locstrings/lib/strings/appexcel.locstring.json';
import { appWord } from 'owa-locstrings/lib/strings/appword.locstring.json';
import {
    getOfficeOnlineAppFromExtension,
    OfficeOnlineApp,
    getExtensionFromFileName,
} from 'owa-file';
import getAttachment from 'owa-attachment-model-store/lib/selectors/getAttachment';
import * as trace from 'owa-trace';
import { SaveToCloudStatus } from 'owa-attachment-savetocloud';
import AttachmentId from 'owa-service/lib/contract/AttachmentId';
import { getODCDriveItem } from 'owa-fileprovider-link-services/lib/getFileInfo/getODCDriveItem';
import { ReferenceAttachmentModel, AttachmentConstants } from 'owa-attachment-model-store';
import ReferenceAttachment from 'owa-service/lib/contract/ReferenceAttachment';
import { getUserConfiguration } from 'owa-session-store';
import showWovenEditInDesktopPremiumUpsellPopup from '../WovenEditInDesktopPremiumUpsellPopup';
import getProjection from 'owa-popout-v2/lib/utils/getProjection';
import { findTabIdBySxSId } from 'owa-tab-store';
import {
    LOADING_STATIC_PAGE,
    LOADING_PAGE_TEXT_ID,
    LOADING_PAGE_SPINNER_ID,
} from '../../constants/wovenEditButtonConstants';
import { confirm } from 'owa-confirm-dialog';
import { logUsage } from 'owa-analytics';

const EXCEL_SVG = require('../../resources/Excel_16x.svg');
const POWERPOINT_SVG = require('../../resources/PowerPoint_16x.svg');
const WORD_SVG = require('../../resources/Word_16x.svg');

const styles = require('./getEditDropDownButton.scss');
const classNames = require('classnames/bind').bind(styles);

export default function getEditDropDownButton(sxsId: string): FilterableMenuItem {
    const currentWindow: Window = getProjection(findTabIdBySxSId(sxsId))?.window || window;
    const sxsStore = getOrCreateSxSStoreData(sxsId);
    let appDisplayName = '';
    let appIcon = '';
    const officeOnlineApp = getOfficeAppFromAttachmentId(sxsStore.extendedViewState.attachmentId);
    switch (officeOnlineApp) {
        case OfficeOnlineApp.Word:
            appDisplayName = appWord;
            appIcon = WORD_SVG;
            break;
        case OfficeOnlineApp.Excel:
            appDisplayName = appExcel;
            appIcon = EXCEL_SVG;
            break;
        case OfficeOnlineApp.PowerPoint:
            appDisplayName = appPowerPoint;
            appIcon = POWERPOINT_SVG;
            break;
        default:
            // no assert never here because officeOnlineApp can be null
            break;
    }
    const officeAppIcon = {
        src: appIcon,
        width: 16,
        height: 16,
        shouldFadeIn: false,
    };

    const editAndReplyButton = new FilterableMenuItem({
        [FilterableMenuItemArgs.name]: loc(EditButtonLabel),
        [FilterableMenuItemArgs.icon]: ControlIcons.Edit,
        [FilterableMenuItemArgs.shouldShow]: () => {
            const isGroup =
                sxsStore.editState.itemId?.mailboxInfo &&
                sxsStore.editState.itemId.mailboxInfo.type === 'GroupMailbox';
            return (
                isFeatureEnabled('wvn-sxsEditButtonSplit') &&
                officeOnlineApp !== null &&
                sxsStore.readingPaneState &&
                sxsStore.viewState?.previewPane &&
                sxsStore.viewState.previewPane.mode === PreviewPaneMode.Wac &&
                sxsStore.editState.editAllowed &&
                sxsStore.editState.mode === EditMode.View &&
                sxsStore.editState.itemId &&
                !sxsStore.editState.isDraft &&
                (!isGroup || !isConsumer()) &&
                !isCloudyAndUsingJsApi(sxsStore)
            );
        },
        [FilterableMenuItemArgs.ariaLabel]: loc(EditButtonLabel),
        [FilterableMenuItemArgs.subMenuProps]: {
            items: [
                {
                    key: 'editInBrowser',
                    text: loc(EditInBrowserButtonLabel),
                    title: loc(EditInBrowserButtonLabel),
                    iconProps: { imageProps: officeAppIcon },
                    onClick: () => {
                        editIn(sxsId, sxsStore, true, currentWindow);
                        logUsage('WovenEditInBrowser', {}, { isCore: true });
                    },
                },
                {
                    key: 'editInDesktop',
                    text: loc(EditInDesktopButtonLabel, loc(appDisplayName)),
                    title: loc(EditInDesktopButtonLabel, loc(appDisplayName)),
                    iconProps: { iconName: ControlIcons.Edit },
                    onClick: () => {
                        if (
                            isConsumer() &&
                            !getUserConfiguration()?.SessionSettings?.IsPremiumConsumerMailbox
                        ) {
                            showWovenEditInDesktopPremiumUpsellPopup(
                                officeOnlineApp,
                                currentWindow
                            );
                            logUsage('WovenEditInDesktopUpsellPopup', {}, { isCore: true });
                        } else {
                            editIn(sxsId, sxsStore, false, currentWindow);
                            logUsage('WovenEditInDesktopPremium', {}, { isCore: true });
                        }
                    },
                    onRenderContent: () => {
                        return (
                            <>
                                <ImageIcon
                                    className={classNames(
                                        'ms-ContextualMenu-icon',
                                        styles.menuIcon
                                    )}
                                    imageProps={officeAppIcon}
                                />
                                <span
                                    className={classNames(
                                        'ms-ContextualMenu-itemText',
                                        styles.menuText
                                    )}>
                                    {loc(EditInDesktopButtonLabel, loc(appDisplayName))}
                                </span>
                                <FontIcon
                                    className={classNames(
                                        'ms-ContextualMenu-icon',
                                        styles.menuIcon,
                                        styles.diamondIcon
                                    )}
                                    iconName={ControlIcons.Diamond}
                                />
                            </>
                        );
                    },
                },
                {
                    key: 'editAndReply',
                    text: loc(EditAndReplyButtonLabel),
                    title: loc(EditAndReplyButtonLabel),
                    iconProps: { iconName: ControlIcons.PencilReply },
                    onClick: () => {
                        editAndReply(sxsId);
                    },
                },
            ],
        },
    });

    return editAndReplyButton;
}

async function editIn(
    sxsId: string,
    sxsStore: SxSStoreData,
    browser: boolean,
    currentWindow: Window
) {
    let newWindow: Window = null;
    try {
        if (browser) {
            newWindow = currentWindow.open('about:blank', '_blank');
            if (!newWindow) {
                throw new Error('Cannnot open new tab');
            }

            newWindow.document.write(LOADING_STATIC_PAGE);
            newTabUpdateMessage(newWindow, loc(EditInBrowserUploadingText));
        }

        if (!sxsStore.extendedViewState.isCloudy) {
            if (sxsStore.extendedViewState.saveToCloudStatus === SaveToCloudStatus.disabled) {
                trace.errorThatWillCauseAlert('woven-editDropDown-saveToCloudDisabled');
                return;
            } else if (
                sxsStore.extendedViewState.saveToCloudStatus === SaveToCloudStatus.available
            ) {
                const saveToCloudFromSxS = await lazySaveToCloudFromSxS.import();
                await saveToCloudFromSxS('SxSCommandBar', sxsId);
            }
        }

        newTabUpdateMessage(newWindow, loc(EditInBrowserOpeningText));

        const attachmentId = sxsStore.extendedViewState.attachmentId;
        const attachment = attachmentId && getAttachment(attachmentId);
        if (attachment) {
            const referenceAttachment = attachment as ReferenceAttachmentModel;
            const referenceAttachmentType = attachment.model as ReferenceAttachment;
            const originalUrl =
                attachment.model.AttachmentOriginalUrl ||
                referenceAttachment.originalUrl ||
                referenceAttachment.openUrl ||
                referenceAttachmentType.AttachLongPathName;

            if (
                !newWindow &&
                ((!sxsStore.extendedViewState.isCloudy &&
                    isConsumer()) /* classic attachment saved to ODC */ ||
                    (sxsStore.extendedViewState.isCloudy &&
                        referenceAttachmentType.ProviderType ===
                            AttachmentConstants.providers
                                .ONE_DRIVE_CONSUMER)) /* cloudy attachment on ODC */
            ) {
                openUrl(
                    (await getODCDriveItem(originalUrl)).webDavUrl,
                    newWindow,
                    attachmentId,
                    currentWindow
                );
            } else {
                openUrl(originalUrl, newWindow, attachmentId, currentWindow);
            }
        }
    } catch (error) {
        if (newWindow) {
            newTabUpdateMessage(
                newWindow,
                loc(EditInBrowserErrorText),
                true /* removeLoadingSpinner */
            );
        } else {
            confirm(loc(EditInBrowserErrorText), null, false /* resolveImmediately */, {
                targetWindow: currentWindow,
                hideCancelButton: true,
            });
        }
    }
}

function openUrl(
    originalUrl: string,
    newWindow: Window,
    attachmentId: AttachmentId,
    currentWindow: Window
) {
    if (newWindow) {
        const querySeparator = originalUrl.indexOf('?') === -1 ? '?' : '&';
        newWindow.location.href = originalUrl + querySeparator + 'web=1';
    } else {
        const officeOnlineApp = getOfficeAppFromAttachmentId(attachmentId);
        const appOpenPrefix =
            officeOnlineApp === OfficeOnlineApp.Word
                ? 'ms-word:ofe|u|'
                : officeOnlineApp === OfficeOnlineApp.Excel
                ? 'ms-excel:ofe|u|'
                : officeOnlineApp === OfficeOnlineApp.PowerPoint
                ? 'ms-PowerPoint:ofe|u|'
                : null;
        currentWindow.location.href = appOpenPrefix + originalUrl;
    }
}

export function getOfficeAppFromAttachmentId(attachmentId: AttachmentId) {
    if (!attachmentId) {
        return null;
    }

    const attachmentModel = attachmentId && getAttachment(attachmentId);
    const extension: string = getExtensionFromFileName(attachmentModel?.model?.Name);
    const officeOnlineApp = getOfficeOnlineAppFromExtension(extension);
    return officeOnlineApp;
}

function newTabUpdateMessage(newWindow: Window, message: string, removeLoadingSpinner?: boolean) {
    if (!newWindow) {
        return;
    }

    const textContainer = newWindow.document.getElementById(LOADING_PAGE_TEXT_ID);
    if (textContainer) {
        textContainer.innerText = message;
    }

    if (removeLoadingSpinner) {
        const spinner = newWindow.document.getElementById(LOADING_PAGE_SPINNER_ID);
        spinner?.parentNode?.removeChild(spinner);
    }
}
